import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  IconAlertTriangle,
  IconCopy,
  IconFileZip,
  IconMarkdown,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { z } from "zod";
import { displayToast } from "../../../components/app/AppToast";
import { Button } from "../../../components/ui/Button";
import {
  uiMessageText,
  type GeneralChatUIMessage,
} from "../../../domains/gpt/hooks/useGptChat";
import { useDragResize } from "../../../hooks/use-drag-resize";
import { createMemoryStore } from "../../../lib/store";
import { cn } from "../../../lib/utils";
import { apiClient, useApiQuery } from "../../../services/api";
import { ConfigTab } from "./ConfigTab";
import { ContextTab } from "./ContextTab";
import { CostTab } from "./CostTab";
import { TraceTab, useTraceTarget } from "./TraceTab";
import {
  deriveTrace,
  firstLine,
  formatBytes,
  formatCount,
  formatCredits,
  formatMs,
  formatPercent,
  isSettled,
  pollInterval,
  summaryMarkdown,
  type RunConfig,
  type RunDebug,
  type Trace,
  type TraceTarget,
} from "./trace";

const MIN_WIDTH = 480;
const MAX_SHARE = 0.95;
const DEFAULT_SHARE = 0.6;
// Below the `sm` breakpoint the drawer takes the whole screen.
const FULL_SCREEN_BELOW = 640;

const TABS = ["trace", "cost", "context", "config"] as const;
type Tab = (typeof TABS)[number];

// Null is the default width, a share of the viewport.
const useWidth = createMemoryStore<number | null>(null);
// Null follows the latest run. A run of another transcript counts as null.
const useSelectedRun = createMemoryStore<string | null>(null);
const useTab = createMemoryStore<Tab>("trace");
const useFlaggedOnly = createMemoryStore(false);

function subscribeResize(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  return () => {
    window.removeEventListener("resize", onChange);
  };
}

// Every run of the transcript, oldest first.
function transcriptRuns(messages: GeneralChatUIMessage[]) {
  const runs: { runId: string; label: string }[] = [];
  for (const message of messages) {
    if (message.metadata === undefined || message.metadata.runId === null)
      continue;
    const runId = message.metadata.runId;
    if (runs.some((run) => run.runId === runId)) continue;
    runs.push({
      runId,
      label: message.role === "user" ? uiMessageText(message) : "Regenerated",
    });
  }
  return runs;
}

async function copy(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    displayToast("The clipboard refused the copy.", "error");
    return;
  }
  displayToast(`${what} copied.`, "success");
}

async function downloadFilesystem(
  conversationId: string,
  run: RunDebug["run"]
): Promise<void> {
  const request = apiClient.get(
    "/assistant/conversations/:conversationId/runs/:runId/debug/filesystem",
    { param: { conversationId, runId: run.id } }
  );
  let response: Awaited<typeof request>;
  try {
    response = await request;
  } catch {
    displayToast("The filesystem download failed.", "error");
    return;
  }
  // Outside the try: a wrong content type is a contract failure, not a network one.
  const blob = z.instanceof(Blob).parse(response.body);
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `assistant-${run.id}-attempt-${String(run.attempt)}.zip`;
  link.click();
  URL.revokeObjectURL(href);
}

// The files each agent saw, zipped in the background once the run settles.
function FilesystemButton({
  conversationId,
  run,
  filesystem,
}: {
  conversationId: string;
  run: RunDebug["run"];
  filesystem: RunDebug["filesystem"];
}): React.ReactElement {
  const icon = <IconFileZip stroke={1.5} />;
  if (filesystem === null)
    return (
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled
        title="A copy is made when the run settles, if the owner has the debug flag."
        leftIcon={icon}
      >
        No filesystem
      </Button>
    );
  switch (filesystem.status) {
    case "pending":
      return (
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled
          leftIcon={icon}
        >
          Preparing filesystem…
        </Button>
      );
    case "ready":
      return (
        <Button
          type="button"
          variant="outline"
          size="xs"
          leftIcon={icon}
          onClick={() => void downloadFilesystem(conversationId, run)}
        >
          {`Filesystem, ${formatBytes(filesystem.bytes)}`}
        </Button>
      );
    case "failed":
      return (
        <Button
          type="button"
          variant="outline"
          size="xs"
          title={filesystem.error}
          leftIcon={icon}
          onClick={() => void copy(filesystem.error, "Filesystem error")}
        >
          Filesystem failed, copy error
        </Button>
      );
    case "expired":
      return (
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled
          leftIcon={icon}
        >
          Filesystem expired
        </Button>
      );
    default:
      throw new Error(filesystem satisfies never);
  }
}

function StatusPill({
  status,
}: {
  status: RunDebug["run"]["status"];
}): React.ReactElement {
  const live = !isSettled(status);
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1 rounded px-1.5 font-mono text-xs",
        status === "failed"
          ? "bg-ds-strawberry-50 text-ds-strawberry-600"
          : live
            ? "bg-ds-pine-50 text-ds-pine-600"
            : "bg-ds-slate-100 text-ds-slate-700"
      )}
    >
      {live && (
        <span className="size-1.5 animate-pulse rounded-full bg-ds-pine-600" />
      )}
      {status}
    </span>
  );
}

function Kpi({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-ds-slate-500">{label}</dt>
      <dd title={value} className="m-0 truncate text-ds-black tabular-nums">
        {value}
      </dd>
    </div>
  );
}

function Body({
  conversationId,
  debug,
  config,
  isFetching,
  onRefresh,
}: {
  conversationId: string;
  debug: RunDebug;
  config: RunConfig | null;
  isFetching: boolean;
  onRefresh: () => void;
}): React.ReactElement {
  const [tab, setTab] = useTab();
  const [flaggedOnly, setFlaggedOnly] = useFlaggedOnly();
  const [, setTarget] = useTraceTarget();
  const trace = deriveTrace(debug, config);
  const run = debug.run;

  function select(target: TraceTarget): void {
    setTarget(target);
    setTab("trace");
  }

  return (
    <>
      <div className="flex shrink-0 flex-col gap-2 border-b border-ds-slate-200 bg-ds-white px-3 py-2">
        <dl className="m-0 grid grid-cols-3 gap-x-3 gap-y-1 font-mono text-xs @xl:grid-cols-5 @3xl:grid-cols-7">
          <Kpi label="duration" value={formatMs(trace.end)} />
          <Kpi
            label="queue"
            value={trace.queue === null ? "n/a" : formatMs(trace.queue)}
          />
          <Kpi label="cost" value={formatCredits(run.credits)} />
          <Kpi label="rounds" value={String(debug.rounds.length)} />
          <Kpi label="tokens in" value={formatCount(run.inputTokens)} />
          <Kpi
            label="cached"
            value={
              run.cachedInputTokens === null
                ? "n/a"
                : run.inputTokens === 0
                  ? formatCount(run.cachedInputTokens)
                  : `${formatCount(run.cachedInputTokens)}, ${formatPercent(run.cachedInputTokens / run.inputTokens)}`
            }
          />
          <Kpi label="tokens out" value={formatCount(run.outputTokens)} />
          <Kpi label="model time" value={formatMs(trace.modelMs)} />
          <Kpi label="tool time" value={formatMs(trace.toolMs)} />
          <Kpi
            label="calls / sources"
            value={`${String(debug.calls.length)} / ${String(trace.sources)}`}
          />
          <Kpi label="attempt" value={String(run.attempt)} />
          <Kpi
            label="intent"
            value={run.intent === null ? "n/a" : run.intent}
          />
          <Kpi
            label="stakes"
            value={run.stakes === null ? "n/a" : run.stakes}
          />
          <div className="flex min-w-0 flex-col">
            <dt className="text-ds-slate-500">alerts</dt>
            <dd className="m-0">
              <button
                type="button"
                aria-pressed={flaggedOnly}
                className={cn(
                  "flex items-center gap-1 rounded px-1 tabular-nums",
                  flaggedOnly
                    ? "bg-ds-clementine-600 text-ds-white"
                    : trace.alerts.length === 0
                      ? "text-ds-slate-500 hover:bg-ds-slate-100"
                      : "text-ds-clementine-600 hover:bg-ds-slate-100"
                )}
                onClick={() => {
                  setFlaggedOnly(!flaggedOnly);
                  setTab("trace");
                }}
              >
                <IconAlertTriangle className="size-3" stroke={2} />
                {flaggedOnly
                  ? `${String(trace.alerts.length)}, flagged only`
                  : String(trace.alerts.length)}
              </button>
            </dd>
          </div>
        </dl>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={run.status} />
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={isFetching}
            leftIcon={<IconRefresh stroke={1.5} />}
            onClick={onRefresh}
          >
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline"
            size="xs"
            leftIcon={<IconCopy stroke={1.5} />}
            onClick={() =>
              void copy(
                JSON.stringify(
                  { debug, config, alerts: trace.alerts },
                  null,
                  2
                ),
                "Diagnostics"
              )
            }
          >
            Copy JSON
          </Button>
          <Button
            type="button"
            variant="outline"
            size="xs"
            leftIcon={<IconMarkdown stroke={1.5} />}
            onClick={() => void copy(summaryMarkdown(debug, trace), "Summary")}
          >
            Copy summary
          </Button>
          <FilesystemButton
            conversationId={conversationId}
            run={run}
            filesystem={debug.filesystem}
          />
          <span className="ml-auto truncate font-mono text-xs text-ds-slate-500">
            {run.id}
          </span>
        </div>
      </div>
      <div
        role="tablist"
        className="flex shrink-0 gap-1 border-b border-ds-slate-200 bg-ds-white px-3"
      >
        {TABS.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={option === tab}
            className={cn(
              "-mb-px border-b-2 px-2 py-1.5 font-mono text-xs capitalize",
              option === tab
                ? "border-ds-blackberry-400 text-ds-black"
                : "border-transparent text-ds-slate-500 hover:text-ds-black"
            )}
            onClick={() => {
              setTab(option);
            }}
          >
            {option}
          </button>
        ))}
      </div>
      <TabPanel
        tab={tab}
        conversationId={conversationId}
        debug={debug}
        config={config}
        trace={trace}
        flaggedOnly={flaggedOnly}
        onSelect={select}
      />
    </>
  );
}

function TabPanel({
  tab,
  conversationId,
  debug,
  config,
  trace,
  flaggedOnly,
  onSelect,
}: {
  tab: Tab;
  conversationId: string;
  debug: RunDebug;
  config: RunConfig | null;
  trace: Trace;
  flaggedOnly: boolean;
  onSelect: (target: TraceTarget) => void;
}): React.ReactElement {
  switch (tab) {
    // The trace scrolls its two columns itself.
    case "trace":
      return (
        <TraceTab
          conversationId={conversationId}
          debug={debug}
          trace={trace}
          flaggedOnly={flaggedOnly}
        />
      );
    case "cost":
      return (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CostTab
            debug={debug}
            config={config}
            trace={trace}
            onSelect={onSelect}
          />
        </div>
      );
    case "context":
      return (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ContextTab config={config} trace={trace} onSelect={onSelect} />
        </div>
      );
    case "config":
      return (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConfigTab debug={debug} config={config} />
        </div>
      );
    default:
      throw new Error(tab satisfies never);
  }
}

function Message({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-start gap-2 p-3 font-mono text-xs text-ds-slate-500">
      {children}
    </div>
  );
}

// One run, polled every second while it moves; the config loads once it starts.
function RunPanel({
  conversationId,
  runId,
}: {
  conversationId: string;
  runId: string;
}): React.ReactElement {
  const query = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/debug",
    {
      param: { conversationId, runId },
      staleTime: 0,
      // No data after a fetch means it failed: "Try again" takes over.
      refetchInterval: (data) =>
        data === undefined ? false : pollInterval(data.body),
    }
  );
  const configQuery = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/debug/config",
    query.state === "success" && query.body.run.startedAt !== null
      ? { param: { conversationId, runId }, staleTime: Infinity }
      : false
  );

  if (query.state === "pending") return <Message>Loading the run…</Message>;
  if (query.state === "error")
    return (
      <Message>
        <p role="alert" className="m-0 text-ds-strawberry-600">
          {query.message}
        </p>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </Message>
    );
  return (
    <>
      {/* A config error costs the rates and prompts, never the live trace. */}
      {configQuery.state === "error" && (
        <div className="flex shrink-0 items-center gap-2 border-b border-ds-slate-200 px-3 py-1 font-mono text-xs">
          <p role="alert" className="m-0 text-ds-strawberry-600">
            Config: {configQuery.message}
          </p>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => void configQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      <Body
        conversationId={conversationId}
        debug={query.body}
        // Null until the config loads: the live trace stays on screen meanwhile.
        config={configQuery.state === "success" ? configQuery.body : null}
        isFetching={query.isFetching}
        onRefresh={() => void query.refetch()}
      />
    </>
  );
}

// Developer diagnostics for one run: where its time, money and context go, live.
export function AssistantDebugDrawer({
  conversationId,
  messages,
  isOpen,
  onClose,
}: {
  conversationId: string;
  messages: GeneralChatUIMessage[];
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement {
  const runs = transcriptRuns(messages);
  const [stored, setSelectedRun] = useSelectedRun();
  const latest = runs.at(-1);
  const runId =
    stored !== null && runs.some((run) => run.runId === stored)
      ? stored
      : latest === undefined
        ? null
        : latest.runId;

  const viewport = useSyncExternalStore(
    subscribeResize,
    () => window.innerWidth
  );
  const maxWidth = Math.round(viewport * MAX_SHARE);
  const defaultWidth = Math.max(
    MIN_WIDTH,
    Math.round(viewport * DEFAULT_SHARE)
  );
  const [storedWidth, setWidth] = useWidth();
  const width =
    storedWidth === null ? defaultWidth : Math.min(maxWidth, storedWidth);
  // The separator sits on the left edge, so the hook drives its x, not the width.
  const resize = useDragResize({
    value: viewport - width,
    min: viewport - maxWidth,
    max: viewport - MIN_WIDTH,
    step: 32,
    reset: viewport - defaultWidth,
    onCommit: (x) => {
      setWidth(viewport - x);
    },
  });

  return createPortal(
    <aside
      aria-label="Assistant debug"
      aria-hidden={!isOpen}
      // Closed, its controls leave the tab order. React 18 has no boolean `inert`.
      inert={isOpen ? undefined : ""}
      className={cn(
        "@container fixed top-0 right-0 z-50 flex h-dvh flex-col bg-ds-cream shadow-xl transition-transform duration-300 ease-in-out will-change-transform",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}
      style={{
        paddingTop: "var(--safe-area-top)",
        // A dragged value, not a design token: the bounds are the constants.
        width:
          viewport < FULL_SCREEN_BELOW ? "100%" : viewport - resize.shownValue,
      }}
    >
      <div
        {...resize.separatorProps}
        aria-label="Resize the debug drawer"
        className="group absolute inset-y-0 -left-1 z-20 hidden w-2 cursor-col-resize justify-center outline-none sm:flex"
      >
        <span
          className={cn(
            "h-full w-0.5 transition-colors duration-150 group-hover:bg-ds-slate-300 group-focus-visible:bg-ds-blackberry-400",
            resize.isDragging ? "bg-ds-blackberry-400" : "bg-transparent"
          )}
        />
      </div>
      <header className="flex shrink-0 items-center gap-2 border-b border-ds-slate-200 bg-ds-white px-3 py-2">
        <p className="m-0 shrink-0 font-dm-sans text-sm font-semibold text-ds-black">
          Assistant debug
        </p>
        {runs.length === 0 ? (
          <p className="m-0 min-w-0 flex-1 text-xs text-ds-slate-500">
            No run yet.
          </p>
        ) : (
          <select
            aria-label="Run"
            className="min-w-0 flex-1 rounded border border-ds-slate-200 bg-ds-white px-1 py-0.5 font-mono text-xs"
            value={runId === null ? "" : runId}
            onChange={(event) => {
              setSelectedRun(event.target.value);
            }}
          >
            {runs.map((run, index) => (
              <option key={run.runId} value={run.runId}>
                {`#${String(index + 1)} · ${firstLine(run.label).slice(0, 80)}`}
              </option>
            ))}
          </select>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Close"
          leftIcon={<IconX stroke={1.5} />}
          onClick={onClose}
        />
      </header>
      {isOpen && runId !== null && (
        <RunPanel key={runId} conversationId={conversationId} runId={runId} />
      )}
    </aside>,
    document.body
  );
}
